import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { createPopper } from '@popperjs/core';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core/lib/Uppy';
import axios from 'axios';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { PublishStatusType, Video } from '../../../../models/video.model';
import { AuthService } from '../../../../services/auth.service';
import * as videoActions from '../../../../state/actions/video.actions';
import { AppState } from '../../../../state/appstate';
import * as videoSelector from '../../../../state/selectors/video.selector';

@Component({
  selector: 'app-my-video',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './my-video.component.html',
})
export class MyVideoComponent implements OnDestroy, AfterViewInit {
  dropdownPopoverShow = false;
  @ViewChild('btnDropdownRef', { static: false })
  btnDropdownRef!: ElementRef;
  @ViewChild('popoverDropdownRef', { static: false })
  popoverDropdownRef: ElementRef | undefined;

  video: Video | null | undefined;
  working: boolean = false;

  id: string | null = null;

  exit$: Subject<void> = new Subject<void>();

  uppy: Uppy = new Uppy({
    restrictions: {
      maxFileSize: 5242880, // 5 Mb
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });
  // dragDropProps = {
  //   proudlyDisplayPoweredByUppy: false,
  //   height: 128,
  //   width: 256,
  //   note: 'Images less than 5 Mb only',
  // };
  dashboardProps = {
    proudlyDisplayPoweredByUppy: false,
    height: 128,
    width: 256,
  };
  showModal: boolean = false;
  dashboardModalProps = {
    target: document.body,
    onRequestCloseModal: (): void => {
      this.showModal = false;
    },
  };

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private route: ActivatedRoute,
    private fbAuth: Auth
  ) {
    this.id = this.route.snapshot.paramMap.get('id');
    this.store
      .pipe(select(videoSelector.getItemById(this.id!)))
      .pipe(takeUntil(this.exit$))
      .subscribe((video) => {
        this.video = video;
        this.uppy.setMeta({ videoId: this.id });
      });
    this.store
      .select((state) => state.videos)
      .pipe(takeUntil(this.exit$))
      .subscribe((videoState) => {
        this.working = videoState.inProgress;
      });

    this.uppy.use(AwsS3, {
      getUploadParameters: this.getUploadParameters.bind(this),
      shouldUseMultipart: false,
    });

    this.uppy.on('upload-success', (file, response) => {
      let tempVideo: Video = {
        ...this.video,
        imageUrl: `https://${
          environment.AWS_VIDEO_IMAGES_CLOUDFRONT
        }/${this.authService.getUid()}/${this.id}/images/${file?.name}`,
      };
      this.store.dispatch(
        videoActions.update({
          video: tempVideo,
        })
      );
    });

    this.uppy.on('upload-error', (file, error, response) => {
      console.error('Upload error:', error);
    });
  }

  async getUploadParameters(file: any): Promise<any> {
    try {
      const fileExtn: string | undefined = file.name?.split('.')?.pop();
      const token: string | undefined =
        await this.fbAuth.currentUser?.getIdToken();

      const response = await axios.get<any>(
        environment['AWS_VIDEO_THUMBNAIL_SIGNEDURL']!,
        {
          params: {
            fileName: file.name,
            fileExtn: fileExtn,
            id: this.video?.id,
          },
          headers: {
            'x-api-key': environment['AWS_API_KEY']!,
            AuthorizationToken: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!(response.status === 200))
        throw new Error('Unsuccessful request', { cause: response });

      // Parse the JSON response.
      const data = await response.data;

      return {
        method: 'PUT',
        url: response.data.url,
        headers: {
          'content-Type': `image/${fileExtn}`,
        },
      };
    } catch (error) {
      console.error(`Error getting upload parameters: ${error}`);
      throw error;
    }
  }

  ngAfterViewInit() {
    createPopper(
      this.btnDropdownRef?.nativeElement,
      this.popoverDropdownRef?.nativeElement,
      {
        placement: 'bottom-start',
      }
    );
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  refreshRecord() {
    this.store.dispatch(videoActions.getVideo({ id: this.id! }));
  }

  publishVideo() {
    this.store.dispatch(
      videoActions.update({
        video: { id: this.id!, publishStatus: PublishStatusType.PUBLISHED },
      })
    );
  }

  updateImage() {}

  deleteVideo() {
    this.store.dispatch(
      videoActions.deleteAction({
        id: this.id!,
      })
    );
  }

  toggleDropdown(event: any) {
    event.preventDefault();
    if (this.dropdownPopoverShow) {
      this.dropdownPopoverShow = false;
    } else {
      this.dropdownPopoverShow = true;
    }
  }
}
